import React from 'react';

import { SELF_REPORTING_ERROR_BOUNDARY } from '@/lib/reactErrorBoundaryReporting';

import { pageSdkErrorKind, reportPageSdkError } from './reportPageSdkError';

export type AiBlockErrorFallbackProps = {
  error: Error;
  reset: () => void;
};

type AiBlockErrorBoundaryProps = {
  children: React.ReactNode;
  /** Render prop for the fallback UI when the generated component throws. */
  renderFallback: (props: AiBlockErrorFallbackProps) => React.ReactNode;
  source: 'page' | 'section';
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
  resetKey?: unknown;
};

type AiBlockErrorBoundaryState = {
  error: Error | null;
  resetKey: unknown;
};

/**
 * Catches errors thrown while rendering an AI-generated component so a broken
 * snippet degrades to a message instead of crashing the whole page.
 *
 * Reset by changing the boundary's `key` or its `resetKey` after an error.
 */
export class AiBlockErrorBoundary extends React.Component<
  AiBlockErrorBoundaryProps,
  AiBlockErrorBoundaryState
> {
  readonly [SELF_REPORTING_ERROR_BOUNDARY] = true;

  state: AiBlockErrorBoundaryState = {
    error: null,
    resetKey: this.props.resetKey
  };

  static getDerivedStateFromError(error: Error): Partial<AiBlockErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: AiBlockErrorBoundaryProps,
    state: AiBlockErrorBoundaryState
  ): Partial<AiBlockErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }

    return null;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
      return;
    }

    reportPageSdkError(error, {
      kind: pageSdkErrorKind(error),
      source: this.props.source,
      componentStack: errorInfo.componentStack,
      telemetryMode: 'remote',
      renderSurface: 'published'
    });
  }

  private reset = (): void => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    if (this.state.error) {
      return this.props.renderFallback({ error: this.state.error, reset: this.reset });
    }

    return this.props.children;
  }
}
